import { useStyles } from './styles';
import Layout from 'components/Layout';
import { CheckboxComponent, CircularIndeterminate, CurrencyInput, LookUpExperiencer, SelectComponent, TextInput, Title } from '@utriper/core-ui';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Fade, FormControl, FormControlLabel, FormLabel, Grid2, IconButton, Paper, Radio, RadioGroup, Slider, Typography } from '@mui/material';
import { ArrowBack, Check } from '@mui/icons-material';
import { Field, Form } from 'react-final-form';
import { useEffect, useMemo, useState } from 'react';
import { Cost } from '@utriper/core';
import { EntityService } from 'core/api/services/lookUp';
import { toast } from 'react-toastify';
import { useCosts } from 'core/contexts/cost';

const EditCostPage = () => {
    const classes = useStyles();
    const { t: translate, i18n } = useTranslation();
    const { loading, loadingSubmit, cost, setCost, getCost, postCost, patchCost } = useCosts();
    const history = useHistory();
    const { id } = useParams();
    const [canEdit, setCanEdit] = useState(false);

    const costId = parseInt(id);

    const costTypeOptions = useMemo(
        () => [
            ...Cost?.getIds().map((id) => {
                const translated = Cost.getTranslatedName(Cost.types, id, i18n.language);
                return { name: translated?.[0] ?? '', id };
            }),
        ],
        [i18n.language]
    );

    const typeValuesOptions = ['percentage', 'unit'];

    const marks = [
        {
            value: 0,
            label: '0%',
        },
        {
            value: 100,
            label: '100%',
        },
    ];

    const onClickBack = () => {
        if (costId) {
            history.push(`/cost/${id}`);
        } else {
            history.push(`/cost`);
        }
    };

    const getInitialValues = (cost) => ({
        ...cost,
        typeValue: cost?.defaultPercentage ? 'percentage' : 'unit',
        experiencerId: cost?.experiencerId ?? null,
    });

    const onSubmit = async (values) => {
        try {
            const { typeValue, name, defaultPrice, defaultCurrency, defaultPercentage, type } = values;
            if (!name) {
                throw new Error(translate('edit_cost.form.errors.name'));
            }
            if (!defaultPercentage && !defaultPrice && !defaultCurrency) {
                throw new Error(translate('edit_cost.form.errors.type_value'));
            }
            if (typeValue === 'percentage' && !defaultPercentage) {
                throw new Error(translate('edit_cost.form.errors.percentage'));
            }

            if (typeValue === 'unit' && !defaultPrice && !defaultCurrency) {
                throw new Error(translate('edit_cost.form.errors.fixed_value'));
            }
            const valuesClon = { ...values };
            if (!type) {
                valuesClon.type = Cost.IDS.system;
            }

            if (typeValue === 'unit' && !defaultCurrency) {
                valuesClon.defaultCurrency = 'USD';
            }

            if (typeValue === 'percentage') {
                valuesClon.defaultCurrency = null;
                valuesClon.defaultPrice = null;
            } else {
                valuesClon.defaultPercentage = null;
            }

            if (costId === 0) {
                const newCost = await postCost(valuesClon);
                if (newCost?.id) {
                    history.push(`/cost/${newCost.id}`);
                    toast.success(translate('edit_cost.form.success.new'), {
                        position: 'top-right',
                        theme: 'colored',
                    });
                } else {
                    history.push('/cost');
                    throw new Error(translate('edit_cost.form.errors.new'));
                }
            } else if (costId && cost?.name) {
                const result = await patchCost(costId, valuesClon);
                if (result) {
                    history.push(`/cost/${costId}`);
                    toast.success(translate('edit_cost.form.success.update'), {
                        position: 'top-right',
                        theme: 'colored',
                    });
                } else {
                    history.push('/cost');
                    throw new Error(translate('edit_cost.form.errors.update'));
                }
            }
        } catch (error) {
            toast.error(error?.message || error, {
                position: 'top-right',
                theme: 'colored',
            });
        }
    };

    useEffect(() => {
        const loadCost = async () => {
            if (costId) {
                const result = await getCost(costId);

                if (!result) {
                    history.push(`/cost/${costId}`);
                }
            } else {
                setCost({});
                setCanEdit(true);
            }
        };

        loadCost();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costId]);

    return (
        <Layout contentClassName={classes.content}>
            {/* Header */}
            <Grid2 container justifyContent="space-between" alignItems="center" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Grid2 container alignItems="center" spacing={2} sx={{ mb: { xs: 2, sm: 0 } }}>
                    <Grid2>
                        <IconButton sx={{ p: { xs: 1, sm: 2 } }} onClick={onClickBack}>
                            <ArrowBack />
                        </IconButton>
                    </Grid2>

                    <Title value={cost?.name ? translate('edit_cost.title.edit') : loading ? translate('loading') : translate('edit_cost.title.new')} />
                </Grid2>
            </Grid2>

            {/* Content */}
            {loading ? (
                <CircularIndeterminate />
            ) : (
                <Grid2 container justifyContent="center" alignItems="center" width="100%" py={2} px={{ xs: 3, sm: 6, md: 8 }}>
                    <Form
                        key={`edit-cost-${costId}`}
                        onSubmit={onSubmit}
                        initialValues={getInitialValues(cost)}
                        render={({ values, handleSubmit, form }) => {
                            const handleChangeTypeValue = (event) => {
                                form.change('typeValue', event?.target?.value);
                            };

                            const handleChangeCurrency = (newCurrency) => {
                                form.change('defaultCurrency', newCurrency);
                                setCanEdit(true);
                            };

                            return (
                                <form key={`edit-cost-${costId}`} onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    <Fade in={true} timeout={300}>
                                        <Grid2 size={{ xs: 12, md: 8 }}>
                                            <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', padding: 3, borderRadius: 2, boxShadow: 'var(--shadow-card)' }}>
                                                <Typography fontSize="1.2em" fontWeight="bold" sx={{ mb: 2 }}>
                                                    {translate('edit_cost.form.title')}
                                                </Typography>
                                                <Grid2 container width="100%" my={2}>
                                                    <Field
                                                        id="name"
                                                        name="name"
                                                        label={`${translate('edit_cost.form.name_label')} *`}
                                                        onChange={(event) => {
                                                            form.change('name', event?.target?.value);
                                                            setCanEdit(true);
                                                        }}
                                                        component={TextInput}
                                                    />
                                                </Grid2>
                                                <Grid2 width="100%" my={2}>
                                                    <Field
                                                        id="experiencerId"
                                                        name="experiencerId"
                                                        render={({ input, meta, ...rest }) => (
                                                            <LookUpExperiencer
                                                                {...rest}
                                                                label={translate('edit_cost.form.experiencer_label')}
                                                                value={values?.experiencerId}
                                                                entityService={EntityService}
                                                                onChange={(newValue) => {
                                                                    input.onChange(newValue);
                                                                    setCanEdit(true);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid2>
                                                <Grid2 container width="100%" my={2}>
                                                    <Field
                                                        id="type"
                                                        name="type"
                                                        label={`${translate('edit_cost.form.type_label')} *`}
                                                        values={costTypeOptions}
                                                        onChange={(event) => {
                                                            form.change('type', event?.target?.value);
                                                            setCanEdit(true);
                                                        }}
                                                        component={SelectComponent}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Grid2>
                                                <Grid2 container width="100%" my={2}>
                                                    <FormControl fullWidth>
                                                        <FormLabel id="type-value-radio-buttons-group-label">{translate('edit_cost.form.type_value')}</FormLabel>
                                                        <RadioGroup row aria-labelledby="type-value-radio-buttons-group-label" name="type-value-radio-buttons-group" value={values.typeValue} onChange={handleChangeTypeValue}>
                                                            {typeValuesOptions.map((typeValue) => (
                                                                <FormControlLabel key={typeValue} value={typeValue} control={<Radio />} label={typeValue === 'percentage' ? translate('edit_cost.form.percentage') : translate('edit_cost.form.fixed_price')} />
                                                            ))}
                                                        </RadioGroup>
                                                    </FormControl>
                                                </Grid2>
                                                <Grid2 container width="100%" my={2}>
                                                    {values?.typeValue === 'percentage' ? (
                                                        <>
                                                            <Typography id="percentage-slider">{`${translate('edit_cost.form.percentage')}: ${values?.defaultPercentage}%`}</Typography>
                                                            <Field
                                                                id="defaultPercentage"
                                                                name="defaultPercentage"
                                                                aria-labelledby="percentage-slider"
                                                                marks={marks}
                                                                min={0}
                                                                step={1}
                                                                max={100}
                                                                render={({ input, meta, ...rest }) => (
                                                                    <Slider
                                                                        {...rest}
                                                                        value={typeof input.value === 'number' ? input.value : 0}
                                                                        onChange={(e, newValue) => {
                                                                            input.onChange(newValue);
                                                                            setCanEdit(true);
                                                                        }}
                                                                        aria-labelledby="percentage-slider"
                                                                    />
                                                                )}
                                                            />
                                                        </>
                                                    ) : (
                                                        <Field
                                                            id="defaultPrice"
                                                            name="defaultPrice"
                                                            label={translate('edit_cost.form.fixed_price')}
                                                            currency={values.defaultCurrency}
                                                            onCurrencyChange={handleChangeCurrency}
                                                            render={({ input, meta, ...rest }) => (
                                                                <CurrencyInput
                                                                    {...rest}
                                                                    value={typeof input.value === 'number' ? input.value : 0}
                                                                    onChange={(newValue) => {
                                                                        input.onChange(newValue);
                                                                        setCanEdit(true);
                                                                    }}
                                                                />
                                                            )}
                                                        />
                                                    )}
                                                </Grid2>
                                                <Grid2 container width="100%" my={2}>
                                                    <Field
                                                        id="isSystem"
                                                        name="isSystem"
                                                        label={translate('edit_cost.form.is_system_label')}
                                                        render={({ input, meta, ...rest }) => (
                                                            <CheckboxComponent
                                                                {...rest}
                                                                checked={input.value}
                                                                onChange={(value) => {
                                                                    input.onChange(value);
                                                                    setCanEdit(true);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Grid2>
                                                <Grid2 container spacing={2} width="100%" mt={4} justifyContent={{ xs: 'center', md: 'flex-end' }}>
                                                    <Grid2 size={{ xs: 6, md: 'auto' }}>
                                                        <Button
                                                            variant="outlined"
                                                            fullWidth
                                                            sx={{ p: 2, color: '#363740 !important', fontWeight: 'bold' }}
                                                            onClick={() => {
                                                                form.reset();
                                                                onClickBack();
                                                            }}>
                                                            {translate('edit_cost.form.cancel_button')}
                                                        </Button>
                                                    </Grid2>
                                                    <Grid2 size={{ xs: 6, md: 'auto' }}>
                                                        <Button
                                                            variant="contained"
                                                            sx={{ p: 2, color: 'white !important', fontWeight: 'bold' }}
                                                            fullWidth
                                                            startIcon={<Check />}
                                                            disabled={!canEdit}
                                                            type="submit"
                                                            onClick={(e) => {
                                                                handleSubmit(e);
                                                            }}
                                                            loading={loadingSubmit}>
                                                            {cost?.name ? translate('edit_cost.form.submit_button.edit') : translate('edit_cost.form.submit_button.new')}
                                                        </Button>
                                                    </Grid2>
                                                </Grid2>
                                            </Paper>
                                        </Grid2>
                                    </Fade>
                                </form>
                            );
                        }}
                    />
                </Grid2>
            )}
        </Layout>
    );
};

export default EditCostPage;
